import { type ClassValue, clsx } from "clsx";
import { readableColor, shade, tint } from "polished";
import { type CSSProperties, useRef } from "react";
import { mergeProps, useFocusRing } from "react-aria";
import { AnimatedTooltip, TruncateText } from "widgets";
import { useTooltip } from "widgets/hooks";
import { getCssColor } from "widgets/utils";

import type { Project } from "../api/graphql.ts";
import { SmartLink } from "../router/SmartLink.tsx";
import style from "./ProjectColoredWidgets.module.css";

// --- Link ---
interface RouterColoredProjectProps {
  project: Pick<Project, "name" | "color" | "id"> | null | undefined;
  alignment?: "right" | "left";
  className?: ClassValue;
  textClassName?: ClassValue;
  forceExternal?: boolean;
  noLink?: boolean;
  style?: CSSProperties;
}

interface BadgeProps {
  color: Project["color"];
  className?: ClassValue;
}

export const ProjectColoredLink = ({
  alignment,
  project,
  className,
  forceExternal,
  noLink,
  textClassName,
  style: propsStyle,
}: RouterColoredProjectProps) => {
  if (!project) {
    return null;
  }

  const align = alignment || "left";

  return (
    <div
      className={clsx(style.container, { [style.container_right]: align === "right" }, className)}
      style={propsStyle}
    >
      {align === "left" && <ProjectMiniBadge color={project.color} className={style.mini_badge_left} />}
      {noLink ? (
        <TruncateText
          text={project.name}
          style={{ flex: 1 }} // If not present, text keeps truncated on table column resize
        />
      ) : (
        <SmartLink
          to={`/project/${project.id}`}
          forceExternal={forceExternal}
          className={clsx(style.default_link, textClassName)}
          style={{ flex: 1 }} // If not present, text keeps truncated on table column resize
        >
          <TruncateText text={project.name} />
        </SmartLink>
      )}
      {align === "right" && <ProjectMiniBadge color={project.color} className={style.mini_badge_right} />}
    </div>
  );
};

export const ProjectMiniBadge = ({ color, className }: BadgeProps) => {
  const hexColor = getCssColor(color);
  return (
    <span style={{ backgroundColor: hexColor }} className={clsx(style.mini_badge, className)} aria-hidden="true" />
  );
};

// --- Badge ---
interface ProjectColoredBadgeProps {
  project: Pick<Project, "name" | "color" | "id" | "description"> | null | undefined;
  size?: "normal" | "small" | "menu"; // TODO Add tiny to replace ProjectColoredLink
  noLink?: boolean;
}

export const ProjectColoredBadge = (props: ProjectColoredBadgeProps) => {
  const triggerRef = useRef<HTMLAnchorElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { triggerProps, tooltipProps, state } = useTooltip(triggerRef, { closeDelay: 0 });
  const { focusProps, isFocusVisible } = useFocusRing();

  const bgColor = getCssColor(props.project?.color);
  const txtColor = readableColor(bgColor, shade(0.6, bgColor), tint(0.6, bgColor), false);

  if (!props.project) {
    return null;
  }

  return (
    <>
      <SmartLink
        to={props.noLink !== true ? `/project/${props.project.id}` : ""}
        className={clsx(style.badge, {
          [style.size_normal]: props.size == undefined || props.size === "normal",
          [style.size_small]: props.size === "small",
          [style.size_menu]: props.size === "menu",
          [style.badge_focused]: isFocusVisible,
        })}
        style={{ backgroundColor: bgColor, color: txtColor }}
        {...mergeProps(triggerProps, focusProps)}
        ref={triggerRef}
      >
        <span className={style.badge_text}>{props.project.name?.slice(0, 2).toUpperCase()}</span>
      </SmartLink>
      <AnimatedTooltip
        title={props.project.name}
        domProps={tooltipProps}
        ref={tooltipRef}
        targetRef={triggerRef}
        state={state}
        offset={4}
        nonInteractive
      >
        {props.project?.description}
      </AnimatedTooltip>
    </>
  );
};
